import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';
import {
    requestUndo,
    requestRedo,
    toggleHistoryPanel,
} from './utils/undoRedo/undoRedoSlice';

interface UndoRedoToolbarProps {
    className?: string;
    showHistoryButton?: boolean;
}

export default function UndoRedoToolbar({
    className = '',
    showHistoryButton = true,
}: UndoRedoToolbarProps) {
    const dispatch = useDispatch();
    const {
        canUndo,
        canRedo,
        isUndoing,
        isRedoing,
        showHistoryPanel,
        history,
    } = useSelector((state: any) => state.undoRedo);

    const handleUndo = () => {
        dispatch(requestUndo());
    };

    const handleRedo = () => {
        dispatch(requestRedo());
    };

    const handleToggleHistory = () => {
        dispatch(toggleHistoryPanel());
    };

    // Get the last action description for tooltip
    const lastAction = history.length > 0 ? history[history.length - 1] : null;
    const nextRedoAction =
        history.length > 0 && canRedo
            ? history[
                  history.findIndex(
                      (_: any, i: number) => i > history.length - 1,
                  )
              ]
            : null;

    return (
        <div className={`flex items-center gap-1 ${className}`}>
            {/* Undo Button */}
            <button
                onClick={handleUndo}
                disabled={!canUndo || isUndoing}
                title={
                    canUndo && lastAction
                        ? `Undo: ${lastAction.description}`
                        : 'Nothing to undo'
                }
                className={`
                    p-2 rounded-md transition-all
                    ${
                        canUndo && !isUndoing
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                            : 'bg-gray-50 text-gray-400 cursor-not-allowed border border-gray-200'
                    }
                    ${isUndoing ? 'animate-pulse' : ''}
                `}
            >
                <ArrowUturnLeftIcon className="h-4 w-4" />
            </button>

            {/* Redo Button */}
            <button
                onClick={handleRedo}
                disabled={!canRedo || isRedoing}
                title={
                    canRedo && nextRedoAction
                        ? `Redo: ${nextRedoAction.description}`
                        : 'Nothing to redo'
                }
                className={`
                    p-2 rounded-md transition-all
                    ${
                        canRedo && !isRedoing
                            ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                            : 'bg-gray-50 text-gray-400 cursor-not-allowed border border-gray-200'
                    }
                    ${isRedoing ? 'animate-pulse' : ''}
                `}
            >
                <ArrowUturnRightIcon className="h-4 w-4" />
            </button>

            {/* History Panel Toggle */}
            {showHistoryButton && (
                <>
                    <div className="w-px h-6 bg-gray-300 mx-1" />
                    <button
                        onClick={handleToggleHistory}
                        title={`${
                            showHistoryPanel ? 'Hide' : 'Show'
                        } History Panel (${history.length} actions)`}
                        className={`
                            relative p-2 rounded-md transition-all
                            ${
                                showHistoryPanel
                                    ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }
                        `}
                    >
                        <ClockIcon className="h-4 w-4" />
                        {history.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                                {history.length > 99 ? '99+' : history.length}
                            </span>
                        )}
                    </button>
                </>
            )}
        </div>
    );
}

// Keyboard shortcut component
export function UndoRedoKeyboardHandler() {
    const dispatch = useDispatch();
    const { canUndo, canRedo, isUndoing, isRedoing } = useSelector(
        (state: any) => state.undoRedo,
    );

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check if we're in an input field
            const target = event.target as HTMLElement;
            const isInInput =
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.contentEditable === 'true';

            // Only handle shortcuts if not in an input field
            if (!isInInput && (event.metaKey || event.ctrlKey)) {
                if (event.key === 'z' && !event.shiftKey) {
                    // Ctrl+Z / Cmd+Z - Undo
                    event.preventDefault();
                    if (canUndo && !isUndoing) {
                        dispatch(requestUndo());
                    }
                } else if (
                    (event.key === 'z' && event.shiftKey) ||
                    event.key === 'y'
                ) {
                    // Ctrl+Shift+Z / Ctrl+Y / Cmd+Shift+Z / Cmd+Y - Redo
                    event.preventDefault();
                    if (canRedo && !isRedoing) {
                        dispatch(requestRedo());
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dispatch, canUndo, canRedo, isUndoing, isRedoing]);

    return null; // This component doesn't render anything
}
